import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { BreakPoints, Button, Spacer, Text, useBreakpoint } from '@cellula/react';
import { primitiveColorTokens, tokens } from '@cellula/react-theme-patient';
import { AnalyticButton, AnalyticProvider } from '@components/analytic_link/mod';
import { getAppDownloadLink } from '@helpers/app_download_link';
import { getAbsoluteRoute } from '@helpers/route';
import { applicationSessionStorage } from '@modules/application_session_storage';
import { I18nAndL10n } from '@modules/i18n_and_l10n/mod';
import { withTranslation } from '@modules/i18n_and_l10n/with_translation';
import { LicenceHolderService } from '@services/licence_holder.service';
import { UserSettingsService } from '@services/user_settings.service';
import { useService } from '@visiba/instantiation';
import { Fragment, Key, MouseEvent, useState } from 'react';
import styled from 'styled-components';
import { UAParser } from 'ua-parser-js';
import { useCurrentLocation } from '../application/use_current_location';
import { LanguageSelect } from './language_select/mod';

interface Props {
	__TEMP__changeLanguage?(langCode: string): void;
}

const elements = {
	container: styled.div`
		position: fixed;
		padding: ${tokens.spacing.x4};
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		display: flex;
		flex-direction: column;
		background: #fff;
		z-index: calc(var(--Z_INDEX_HACK, 0));
		text-align: center;
	`,
	logo: styled.div`
		display: flex;
		justify-content: center;
		height: 50px;
		margin-top: ${tokens.spacing.x4};

		img {
			max-height: 100%;
		}
	`,
	fakeButton: styled.a`
		display: flex;
		justify-content: center;
	`,
	OLD_useNewReceptionPage_languageSelectContainer: styled.div`
		display: flex;
		flex-direction: row;
		justify-content: flex-end;
		text-align: center;
		width: 100%;
	`,
	languageSelectContainer: styled.div`
		display: flex;
		flex-direction: row;
		justify-content: center;
		text-align: center;
		width: 100%;
	`,
};

export function DeviceGateway({ __TEMP__changeLanguage }: Props): JSX.Element | null {
	const userSettingsService = useService(UserSettingsService);
	const licenceHolderService = useService(LicenceHolderService);
	const analyticService = useService(AnalyticService);
	const [, config] = useCurrentLocation();
	const breakpoint = useBreakpoint();

	const parser = new UAParser();
	const deviceType = parser.getDevice().type;
	const queryParams = new URLSearchParams(location.search);
	const userIgnoresPrompt = queryParams.get('skipPrompt') === 'true';
	const userIsOnDesktop = deviceType !== 'tablet' && deviceType !== 'mobile';
	const userIsGuestParticipant = location.pathname.includes('/guest-participant');
	const appDownloadLink = getAppDownloadLink(config?.appConfig ?? null);
	const isMobileBrowserFriendly =
		location.pathname.includes('/visits') ||
		location.pathname.includes('/cookie-policy') ||
		location.pathname.includes('/appointment') ||
		location.pathname.includes('/sign-in');
	const showLanguageSelect = config?.languages.length ?? 0 > 1;
	const shouldBlockRouteOnMobile = !(location.pathname.includes('/dropin') && breakpoint === BreakPoints.Small);

	const [hidden, setHidden] = useState(userIgnoresPrompt || userIsOnDesktop || userIsGuestParticipant);

	if (window.__visiba__.allowMobile) {
		return null;
	}

	function handleContinueInBrowserClick(): void {
		applicationSessionStorage.set('hideGatewayOnMobileFriendly', true);

		if (isMobileBrowserFriendly) {
			setHidden(true);
		} else {
			window.location.href = getAbsoluteRoute(`${licenceHolderService.alias}/visits?skipPrompt=true`);
		}
	}

	function handleAppButtonClick(_: MouseEvent<HTMLAnchorElement>): void {
		analyticService.trackEvent(AnalyticCategories.DeviceGateway, 'Clicked to download the app');
	}

	function handleLanguageChange(code: Key): void {
		const convertedCode = I18nAndL10n.convertExternalLocaleToAvailableLocale(code as string);

		// TODO: REMOVE THIS ONCE NOT INJECTED ANY MORE
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		//@ts-ignore
		analyticService.trackEvent(AnalyticCategories.DeviceGateway, `Changed language to: ${convertedCode}`);

		userSettingsService.updateSettings({ selectedLanguage: convertedCode });

		if (__TEMP__changeLanguage) {
			__TEMP__changeLanguage(code as string);
		}
	}

	if (
		hidden ||
		(applicationSessionStorage.get('hideGatewayOnMobileFriendly') && isMobileBrowserFriendly) ||
		shouldBlockRouteOnMobile
	) {
		return null;
	}

	return (
		<AnalyticProvider category={AnalyticCategories.DeviceGateway}>
			<elements.container>
				<elements.logo>
					<img src={(config?.theme.EmailLogoUrl as string) ?? (config?.theme.LogoUrl as string) ?? ''} alt='' />
				</elements.logo>
				<Spacer vertical={tokens.spacing.x7} />
				<Text variant='bodyRegular' color={primitiveColorTokens.neutral[1000]}>
					{withTranslation('PW.Component.DeviceGateway.Description')}
				</Text>

				<Spacer vertical={tokens.spacing.x7} />

				{appDownloadLink != null ? (
					// TODO CEL-31: This should be one component from @cellula/react
					<elements.fakeButton onClick={handleAppButtonClick} href={appDownloadLink}>
						<Button variant='primary' size='xLarge'>
							{withTranslation('PW.Component.DeviceGateway.Button.DownloadApp')}
						</Button>
					</elements.fakeButton>
				) : null}
				<Spacer vertical={tokens.spacing.x2} />
				{isMobileBrowserFriendly ? (
					<AnalyticButton variant='ghost' size='xLarge' onClick={handleContinueInBrowserClick} action='Clicked to go to patient web'>
						{withTranslation('PW.Component.DeviceGateway.Button.ContinueInBrowser')}
					</AnalyticButton>
				) : null}

				{showLanguageSelect ? (
					<Fragment>
						<Spacer vertical={tokens.spacing.x7} />
						<elements.languageSelectContainer>
							<LanguageSelect
								onChange={handleLanguageChange}
								defaultValue={userSettingsService.getSettings().selectedLanguage}
								languages={
									config?.languages.map((language) => ({
										code: language.code,
										nativeName: language.nativeName,
										key: language.code,
									})) ?? []
								}
							/>
						</elements.languageSelectContainer>
					</Fragment>
				) : null}
			</elements.container>
		</AnalyticProvider>
	);
}
